import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    "dropzone",
    "fileInput",
    "fileInfo",
    "fileName",
    "fileSize",
    "previewButton",
    "uploadForm",
    "checkbox",
    "selectAll",
    "importButton",
    "rowCounter"
  ]

  connect() {
    this.selectedRows = new Set()
    this.editedData = new Map()
  }

  // Stimulus target callback - fires when each checkbox is connected to DOM
  checkboxTargetConnected(checkbox) {
    // If checkbox is pre-checked, add it to selectedRows
    if (checkbox.checked) {
      const rowId = checkbox.dataset.rowId
      this.selectedRows.add(rowId)
      this.updateSelectAllState()
      this.updateImportButton()
    }
  }

  // Stimulus target callback - fires when checkbox is removed from DOM
  checkboxTargetDisconnected(checkbox) {
    const rowId = checkbox.dataset.rowId
    this.selectedRows.delete(rowId)
  }

  disconnect() {
    this.selectedRows.clear()
    this.editedData.clear()
  }

  // File Dialog
  openFileDialog(event) {
    event.preventDefault()
    this.fileInputTarget.click()
  }

  // File Selection
  handleFileSelect() {
    const file = this.fileInputTarget.files[0]
    if (file) {
      this.displayFileInfo(file)
    }
  }

  // Drag & Drop
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add('border-primary', 'bg-blue-50')
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-blue-50')
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-blue-50')

    const files = event.dataTransfer.files
    if (files.length > 0 && files[0].name.endsWith('.csv')) {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(files[0])
      this.fileInputTarget.files = dataTransfer.files
      this.displayFileInfo(files[0])
    }
  }

  // Display File Info
  displayFileInfo(file) {
    const sizeInKB = (file.size / 1024).toFixed(2)
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2)
    const sizeText = file.size > 1024 * 1024 ? `${sizeInMB} MB` : `${sizeInKB} KB`

    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = sizeText
    this.fileInfoTarget.classList.remove('hidden')
    this.previewButtonTarget.classList.remove('hidden')
  }

  // Remove File
  removeFile(event) {
    event.preventDefault()
    this.fileInputTarget.value = ''
    this.fileInfoTarget.classList.add('hidden')
    this.previewButtonTarget.classList.add('hidden')
  }

  // Handle Preview Submission
  handlePreview(event) {
    // Let Rails UJS handle the form submission
    // The response will be a Turbo Stream that updates the preview container
  }

  // Row Selection
  toggleRow(event) {
    const checkbox = event.currentTarget
    const rowId = checkbox.dataset.rowId

    if (checkbox.checked) {
      this.selectedRows.add(rowId)
    } else {
      this.selectedRows.delete(rowId)
    }

    this.updateSelectAllState()
    this.updateImportButton()
  }

  toggleSelectAll(event) {
    const isChecked = event.currentTarget.checked

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = isChecked
      const rowId = checkbox.dataset.rowId

      if (isChecked) {
        this.selectedRows.add(rowId)
      } else {
        this.selectedRows.delete(rowId)
      }
    })

    this.updateImportButton()
  }

  updateSelectAllState() {
    if (this.hasSelectAllTarget) {
      const allChecked = this.checkboxTargets.length > 0 &&
        this.checkboxTargets.every(cb => cb.checked)
      this.selectAllTarget.checked = allChecked
    }
  }

  updateImportButton() {
    if (this.hasImportButtonTarget) {
      const count = this.selectedRows.size
      if (count > 0) {
        this.importButtonTarget.disabled = false
        // Use i18n text from data attributes (handles pluralization)
        const textKey = count === 1 ? 'importCountTextOne' : 'importCountTextOther'
        const template = this.importButtonTarget.dataset[textKey] || this.importButtonTarget.dataset.importText
        // Replace %{count} placeholder
        this.importButtonTarget.textContent = template.replace(/%\{count\}/g, count)
      } else {
        this.importButtonTarget.disabled = true
        this.importButtonTarget.textContent = this.importButtonTarget.dataset.importText
      }
    }
  }

  // Inline Editing
  handleCellEdit(event) {
    const cell = event.currentTarget
    const rowId = cell.dataset.rowId
    const field = cell.dataset.field
    const newValue = cell.textContent.trim()

    if (!this.editedData.has(rowId)) {
      this.editedData.set(rowId, {})
    }
    this.editedData.get(rowId)[field] = newValue
  }

  // Duplicate Action Selection
  handleDuplicateAction(event) {
    const select = event.currentTarget
    const rowId = select.dataset.rowId
    const action = select.value

    if (!this.editedData.has(rowId)) {
      this.editedData.set(rowId, {})
    }
    this.editedData.get(rowId).duplicate_action = action
  }

  // Submit Import
  async handleImport(event) {
    event.preventDefault()

    const button = event.currentTarget
    const originalHTML = button.innerHTML

    // Show loading state
    button.disabled = true
    const importingText = button.dataset.importingText || 'Importing...'
    button.innerHTML = `
      <i class="fa-solid fa-spinner fa-spin mr-2"></i>
      ${importingText}
    `

    try {
      const rows = []
      this.selectedRows.forEach(rowId => {
        const rowElement = document.querySelector(`tr[data-row-id="${rowId}"]`)

        // Parse full row data from data attribute (preserves all CSV fields)
        // Need to decode HTML entities first (e.g., &quot; -> ")
        const textarea = document.createElement('textarea')
        textarea.innerHTML = rowElement.dataset.rowData
        const rowData = JSON.parse(textarea.value)
        rowData.id = rowId
        rowData.duplicate_action = this.getDuplicateAction(rowId)

        // Apply any edited values from contenteditable cells
        if (this.editedData.has(rowId)) {
          Object.assign(rowData, this.editedData.get(rowId))
        }

        rows.push(rowData)
      })

      // Submit via fetch to handle redirect properly
      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const response = await fetch(button.dataset.importUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ rows: rows })
      })

      if (response.ok) {
        const data = await response.json()
        window.location.href = data.redirect_url
      } else {
        // Restore button on error
        button.disabled = false
        button.innerHTML = originalHTML
        alert('Une erreur est survenue lors de l\'import')
      }
    } catch (error) {
      // Restore button on error
      button.disabled = false
      button.innerHTML = originalHTML
      alert('Une erreur est survenue lors de l\'import')
    }
  }

  getCellValue(rowId, field) {
    const cell = document.querySelector(`[data-row-id="${rowId}"][data-field="${field}"]`)
    return cell ? cell.textContent.trim() : ''
  }

  getDuplicateAction(rowId) {
    const select = document.querySelector(`select[data-row-id="${rowId}"]`)
    // Only duplicate rows have a select dropdown
    // Valid rows should default to 'import', not 'skip'
    return select ? select.value : 'import'
  }
}
